import { Icon } from '@design-system/index'
import { css } from '@emotion/react'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import { isBefore } from 'date-fns'
import type { ReactNode } from 'react'

import { Badge } from '@/component/atoms/Badge'
import type { NavInfoList } from '@/component/organisms/Navigation/SideNavigation/SideNavigation'
import { FeaturePermission } from '@/models/(Permission)/FeaturePermission'
import type { RoutePermissionType } from '@/models/(Permission)/RoutePermission.type'
import { RouteName } from '@/router/RouteName'

type ProfileNavActions = {
  scienceflatConnect?: () => void
}

type ProfileNavItem =
  | {
      label?: string
      labelContent?: ReactNode
      action?: () => void
      to?: string
      isNew?: boolean
      showInSidebar?: boolean
      disabled?: boolean
    }
  | false
  | undefined

export type ProfileNavInfoList = {
  title?: string
  items: ProfileNavItem[]
}[]

type SideNavCompatibleItem = { label: string; to: string; isNew?: boolean }

export const toSideNavInfoList = (list: ProfileNavInfoList): NavInfoList =>
  list.map((group) => ({
    ...group,
    items: group.items.filter(
      (item): item is SideNavCompatibleItem =>
        !!item && item.showInSidebar !== false && !!item.label && !!item.to,
    ),
  }))

export const getMyPageNavigation = ({
  permission,
  actions,
  featurePermission = new FeaturePermission([]),
}: {
  permission: RoutePermissionType
  actions?: ProfileNavActions
  featurePermission?: FeaturePermission
}): ProfileNavInfoList => {
  const navInfoList: ProfileNavInfoList = [
    {
      items: [
        permission.myinfo.사이언스플랫.access &&
          featurePermission.isAllowed('CONTENT_SCIENCEFLAT') && {
            labelContent: (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 4px;
                `}
              >
                <img
                  src={s3URL.mathflatAssetsBucket.teacher('images/logos/scienceflat_logo.svg')}
                  width={90}
                  height={24}
                  alt="사이언스플랫"
                />
                <Icon name="share" size={16} color="var(--Colors-Green-500)" />
              </div>
            ),
            action: actions?.scienceflatConnect,
            to: RouteName.myUserInfo,
            showInSidebar: false,
          },

        permission.myinfo.잉글리시플랫.access &&
          featurePermission.isAllowed('CONTENT_ENGLISHFLAT') && {
            labelContent: (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 4px;
                  opacity: 0.5;
                `}
              >
                <img
                  src={s3URL.mathflatAssetsBucket.teacher('images/logos/englishflat_logo.svg')}
                  width={90}
                  height={24}
                  alt="잉글리시플랫"
                />
                <Badge backgroundColor="var(--Colors-Indigo-500)" size="xs" radius="100">
                  Coming Soon
                </Badge>
              </div>
            ),
            showInSidebar: false,
            disabled: true,
          },
        permission.myinfo.기관정보.access && {
          label: '내 정보',
          to: RouteName.myUserInfo,
        },
        permission.myinfo.이용현황.access &&
          featurePermission.isAllowed('PAYMENT_READ') && {
            label: '이용 현황',
            to: RouteName.myStatus,
          },
        permission.myinfo.교재비정산.access && {
          label: '교재비 정산',
          to: RouteName.mySettlement,
        },
      ],
    },
    {
      title: '고객지원',
      items: [
        permission.고객지원.access && {
          label: '시중교재 지원 정보',
          to: RouteName.customerSupportedWorkbook,
          isNew: isBefore(new Date(), new Date(2026, 3, 1)),
        },
        permission.고객지원.access && {
          label: '공지 및 업데이트',
          to: RouteName.customerNotice,
        },
        permission.고객지원.access && {
          label: '1:1 문의',
          to: RouteName.customerQna,
        },
        permission.고객지원.access && {
          label: '자주 묻는 질문',
          to: RouteName.customerFaq,
        },
        permission.myinfo.사용가이드.access && {
          label: '사용 가이드',
          to: RouteName.guide,
        },
      ],
    },
    ...(permission.VOD_칼럼.access
      ? [
          {
            title: '학원 운영 팁',
            items: [
              permission.manage.studentApp.access && {
                label: 'VOD · 칼럼 · 후기',
                to: RouteName.vodColumn,
              },
            ],
          },
        ]
      : []),
  ]

  return navInfoList
}

export const supportedWorkbookSubNavigationLabels = [
  '시중교재 지원 목록',
  '시중교재 22개정 지원 계획',
] as const
